/**
 * The skill editor (B2). It edits a draft the agent distilled from a chat, a
 * draft written from scratch, the user's own live skill, or (for a Manager or
 * Realm admin) a Company-level one. Four tabs: Overview (name, description,
 * when to use it), SKILL.md (the instructions), Files (what the skill
 * carries) and Test (a new chat that installs the skill and runs a request).
 *
 * The form starts from the stored files and is the dialog's own state. When
 * the stored skill changes underneath it (the agent distilled the draft
 * again), an untouched form takes the new version; an edited one says so
 * and lets the user choose.
 */
import React, { useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  IconButton, List, ListItem, ListItemButton, ListItemText, Tab, Tabs, TextField, Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import DeleteIcon from '@mui/icons-material/Delete'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import DisabledReason from '../system/DisabledReason'
import { decodeBase64Text, draftFromBundle, readFileAsBase64, SKILL_SCAFFOLD } from '../../utils/skillMarkdown'
import type { SkillBundleFile } from '../../services/agentSkillsApi'
import type { SkillDraft, SkillInfo, SkillTestRecord } from '../../types/agent'

export interface SkillDraftDialogProps {
  /** The stored skill being edited; null for "Write one from scratch". */
  skill: SkillInfo | null
  /** The stored skill's files; undefined while they load. */
  files: SkillBundleFile[] | undefined
  error: string | null
  saving: boolean
  testing: boolean
  /** Why Run a test chat is unavailable here (no chat jobs on Admin), or undefined. */
  testDisabledReason?: string
  /** Save the form as the draft (creates it when new); resolves to the stored row. */
  onSaveDraft: (draft: SkillDraft) => Promise<SkillInfo>
  /** Save the form, then save the draft to My skills. */
  onPublish: (draft: SkillDraft) => Promise<unknown>
  /** Save the form over a live skill. */
  onSaveChanges: (draft: SkillDraft) => Promise<unknown>
  /** Bayes Price Manager: the names of the Bayes Price skills. A draft named like one can be saved over it. */
  bayesPriceNames?: string[]
  /** Save the form, then save the draft over the Bayes Price skill of the same name. Absent: not offered. */
  onPublishToBayesPrice?: (draft: SkillDraft) => Promise<unknown>
  /** A draft: discard it. New: just close. */
  onDiscard: () => void
  onRunTest: (draft: SkillDraft, prompt: string) => Promise<unknown>
  onOpenTest?: (sessionId: string) => void
  onClose: () => void
}

type EditorTab = 'overview' | 'skill-md' | 'files' | 'test'

const NAME_PATTERN = /^[a-z0-9]+(-[a-z0-9]+)*$/
const EMPTY_DRAFT: Required<SkillDraft> = { name: '', description: '', triggers: [], body: SKILL_SCAFFOLD, files: [] }

/** Where a file the user adds goes in the bundle: files/<its name>. */
const addedFilePath = (fileName: string) => `files/${fileName.replace(/[\\/]/g, '_')}`

/** Why the form cannot be saved, or undefined. Mirrors the server's checks. */
/** A skill has no install script: every chat already has the same software. */
export const NO_INSTALL_SCRIPT = 'A skill has no install script: every chat already has the same software. Describe in SKILL.md how to use what is installed.'
const isInstallScript = (path: string) => (path.split(/[\\/]/).pop() ?? '').toLowerCase() === 'install.sh'

export function draftProblem(form: SkillDraft): string | undefined {
  if (!NAME_PATTERN.test(form.name) || form.name.length > 64) return 'The name must be lowercase words joined by hyphens, like brand-check.'
  if (!form.description.trim()) return 'Add a description: it is how the agent decides when to use the skill.'
  if (!form.body.trim()) return 'Write the instructions on the SKILL.md tab.'
  return undefined
}

const same = (a: SkillDraft, b: SkillDraft) => JSON.stringify(a) === JSON.stringify(b)

const isBinaryPath = (path: string) => /\.(pptx|potx|xlsx|docx|png|jpe?g|gif|webp|pdf|zip|gz|tar|woff2?|ttf|ico)$/i.test(path)

function testsOf(skill: SkillInfo | null): SkillTestRecord[] {
  const tests = skill?.manifest?.tests
  return Array.isArray(tests) ? (tests as SkillTestRecord[]).slice().reverse() : []
}

export default function SkillDraftDialog(props: SkillDraftDialogProps) {
  const { skill, files, error, onClose } = props
  const loading = Boolean(skill) && !files && !error
  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { height: 'min(780px, 90vh)' } }}>
      {loading || (skill && error) ? (
        <>
          <DialogTitle>{skill?.name}</DialogTitle>
          <DialogContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {error ? <Alert severity="error">{error}</Alert> : <CircularProgress size={28} />}
          </DialogContent>
          <DialogActions><Button color="info" onClick={onClose}>Close</Button></DialogActions>
        </>
      ) : (
        // Keyed by the skill so a different skill starts a fresh form.
        <SkillDraftForm key={skill?.id ?? 'new'} {...props} />
      )}
    </Dialog>
  )
}

function SkillDraftForm({
  skill, files, saving, testing, testDisabledReason,
  bayesPriceNames, onPublishToBayesPrice,
  onSaveDraft, onPublish, onSaveChanges, onDiscard, onRunTest, onOpenTest, onClose,
}: SkillDraftDialogProps) {
  const isNew = !skill
  const isDraft = isNew || skill.status === 'draft'
  const initial = skill && files ? draftFromBundle(skill, files) : EMPTY_DRAFT
  const [tab, setTab] = useState<EditorTab>('overview')
  const [form, setForm] = useState<Required<SkillDraft>>(initial)
  // What the form was loaded from (or last saved as), and the stored version it matches.
  const [baseline, setBaseline] = useState<Required<SkillDraft>>(initial)
  const [loadedHash, setLoadedHash] = useState(skill?.content_hash ?? '')
  const [triggerInput, setTriggerInput] = useState('')
  const [prompt, setPrompt] = useState('')
  const [openFile, setOpenFile] = useState<string | null>(null)

  const dirty = !same(form, baseline)
  const problem = draftProblem(form)

  // The stored skill moved on (a re-hoist). Its new files have loaded when
  // `files` is defined, since the bundle query is keyed by the content hash.
  const storedChanged = Boolean(skill && files && skill.content_hash !== loadedHash)
  const adoptStored = () => {
    if (!skill || !files) return
    const next = draftFromBundle(skill, files)
    setForm(next)
    setBaseline(next)
    setLoadedHash(skill.content_hash)
  }
  if (storedChanged && !dirty) adoptStored()

  const update = (patch: Partial<SkillDraft>) => setForm({ ...form, ...patch })
  // A file from the user's computer (a template, a manifest) joins the bundle
  // on the next save; one with the same name is replaced.
  const [fileError, setFileError] = useState<string | null>(null)
  const handleAddFiles = async (list: FileList | null) => {
    if (!list || list.length === 0) return
    setFileError(null)
    if (Array.from(list).some((f) => isInstallScript(f.name))) {
      setFileError(NO_INSTALL_SCRIPT)
      return
    }
    try {
      const added = await Promise.all(Array.from(list).map(async (f) => ({ path: addedFilePath(f.name), content: await readFileAsBase64(f) })))
      setForm((current) => ({ ...current, files: [...current.files.filter((x) => !added.some((a) => a.path === x.path)), ...added] }))
    } catch (e) {
      setFileError(e instanceof Error ? e.message : 'Could not read the file')
    }
  }
  const installedPath = (path: string) => `.claude/skills/${form.name || '<name>'}/${path}`
  const addTrigger = () => {
    const t = triggerInput.trim()
    if (t && !form.triggers.includes(t)) update({ triggers: [...form.triggers, t] })
    setTriggerInput('')
  }

  const handleSaveDraft = async () => {
    const saved = await onSaveDraft(form)
    setBaseline(form)
    setLoadedHash(saved.content_hash)
  }

  const tests = testsOf(skill)
  const bayesPrice = skill?.visibility === 'public'
  // Matt's loop: hoist, test, then save the draft over the Bayes Price skill it improves.
  const savesToBayesPrice = isDraft && Boolean(onPublishToBayesPrice) && Boolean(bayesPriceNames?.includes(form.name))
  const titleVerb = isNew ? 'New skill' : isDraft ? 'Draft' : 'Edit skill'
  // A live skill is tested as saved: writing an untested form over it first
  // would hand the edit to everyone already using it.
  const liveEdited = !isDraft && dirty
    ? 'A test chat runs this skill as saved. Save or undo your changes first, or make a copy and test the copy.'
    : undefined
  const testReason = testDisabledReason ?? liveEdited ?? (prompt.trim() ? problem : 'Type what to ask in the test chat.')

  return (
    <>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" fontWeight="bold" noWrap>{titleVerb}{form.name ? `: ${form.name}` : ''}</Typography>
          <Typography variant="body2" color="text.secondary">
            {isDraft
              ? 'Only you can see a draft. Check it, try it in a test chat, then save it to My skills.'
              : bayesPrice
                ? 'Chats of every customer use the saved version from their next start, unless a customer has turned it off or has a skill of the same name.'
                : 'Chats that already installed this skill keep their copy until they install it again.'}
          </Typography>
        </Box>
        {skill?.visibility === 'organizational' && <Chip label="Company-level" size="small" variant="outlined" />}
        {bayesPrice && <Chip label="Bayes Price" size="small" color="success" variant="outlined" />}
        <IconButton size="small" onClick={onClose} aria-label="Close the skill editor"><CloseIcon fontSize="small" /></IconButton>
      </DialogTitle>
      {storedChanged && dirty && (
        <Alert
          severity="info"
          sx={{ borderRadius: 0 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="info" size="small" onClick={adoptStored}>Load the new version</Button>
              <Button color="info" size="small" onClick={() => setLoadedHash(skill!.content_hash)}>Keep my edits</Button>
            </Box>
          }
        >
          The agent has written a new version of this draft.
        </Alert>
      )}
      <Tabs value={tab} onChange={(_, v: EditorTab) => setTab(v)} sx={{ px: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="overview" label="Overview" />
        <Tab value="skill-md" label="SKILL.md" />
        <Tab value="files" label={`Files (${form.files.length})`} />
        <Tab value="test" label="Test" />
      </Tabs>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '16px !important' }}>
        {tab === 'overview' && (
          <>
            <TextField
              label="Name"
              value={form.name}
              onChange={(e) => update({ name: e.target.value.trim() })}
              helperText="Lowercase words joined by hyphens, like brand-check."
              error={Boolean(form.name) && !NAME_PATTERN.test(form.name)}
              size="small"
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => update({ description: e.target.value })}
              helperText="One sentence. The agent reads it to decide when the skill applies."
              multiline
              minRows={2}
              size="small"
            />
            <Box>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>When to use it</Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Phrases a request might contain, like “check the brand”.
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 1 }}>
                {form.triggers.map((t) => (
                  <Chip key={t} label={t} size="small" onDelete={() => update({ triggers: form.triggers.filter((x) => x !== t) })} />
                ))}
              </Box>
              <TextField
                size="small"
                fullWidth
                placeholder="Add a phrase and press Enter"
                value={triggerInput}
                onChange={(e) => setTriggerInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTrigger() } }}
                onBlur={addTrigger}
                slotProps={{ htmlInput: { 'aria-label': 'Add a phrase' } }}
              />
            </Box>
          </>
        )}

        {tab === 'skill-md' && (
          <TextField
            label="Instructions (SKILL.md)"
            value={form.body}
            onChange={(e) => update({ body: e.target.value })}
            multiline
            minRows={18}
            fullWidth
            slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } } }}
            helperText="What the skill does, when it applies, and the steps. A future chat follows this cold."
          />
        )}

        {tab === 'files' && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
              <Typography variant="body2" color="text.secondary">
                The files the skill carries: templates, manifests, scripts, reference data. Name each one in the SKILL.md Files section by the path shown, and say which step uses it.
              </Typography>
              <Button component="label" color="info" variant="outlined" size="small" startIcon={<UploadFileIcon />} sx={{ flexShrink: 0 }}>
                Add a file
                <input
                  type="file"
                  hidden
                  multiple
                  aria-label="Add a file"
                  onChange={(e) => { void handleAddFiles(e.target.files); e.target.value = '' }}
                />
              </Button>
            </Box>
            {fileError && <Alert severity="error">{fileError}</Alert>}
            {form.files.length === 0 ? (
              <Typography variant="body2" color="text.secondary">This skill carries no files besides SKILL.md.</Typography>
            ) : (
              <List disablePadding>
                {form.files.map((f) => (
                  <Box key={f.path}>
                    <ListItem
                      disablePadding
                      secondaryAction={
                        <IconButton size="small" aria-label={`Remove ${f.path}`} onClick={() => update({ files: form.files.filter((x) => x.path !== f.path) })}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      }
                    >
                      <ListItemButton onClick={() => setOpenFile(openFile === f.path ? null : f.path)}>
                        <ListItemText
                          primary={f.path}
                          secondary={isInstallScript(f.path) ? 'Never run: skills have no install script. Remove it, and describe in SKILL.md how to use what is installed.' : installedPath(f.path)}
                          primaryTypographyProps={{ fontFamily: 'monospace', fontSize: 13 }}
                          secondaryTypographyProps={{ fontFamily: 'monospace', fontSize: 12 }}
                        />
                      </ListItemButton>
                    </ListItem>
                    {openFile === f.path && (
                      isBinaryPath(f.path) ? (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 1, fontStyle: 'italic' }}>Binary file: preview not shown.</Typography>
                      ) : (
                        <Box component="pre" sx={{ m: 0, mb: 1, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12, whiteSpace: 'pre-wrap', maxHeight: 240, overflow: 'auto' }}>
                          {decodeBase64Text(f.content)}
                        </Box>
                      )
                    )}
                  </Box>
                ))}
              </List>
            )}
          </>
        )}

        {tab === 'test' && (
          <>
            <Typography variant="body2" color="text.secondary">
              {isDraft
                ? 'A test saves your draft, opens a new chat on the jobs this chat uses, installs the draft there and asks your request.'
                : 'A test opens a new chat on the jobs this chat uses, installs the skill as saved and asks your request.'}
              {' '}It uses AI credits like any message.
            </Typography>
            <TextField
              label="What to ask in the test chat"
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              multiline
              minRows={3}
              fullWidth
            />
            <Box>
              <DisabledReason reason={testReason}>
                <Button
                  variant="contained"
                  color="info"
                  startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <PlayArrowIcon />}
                  disabled={testing}
                  onClick={() => onRunTest(form, prompt)}
                >
                  Run a test chat (uses AI credits)
                </Button>
              </DisabledReason>
            </Box>
            {tests.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>Earlier test chats</Typography>
                <List dense disablePadding>
                  {tests.map((t) => (
                    <ListItemButton key={t.session_id} onClick={onOpenTest ? () => onOpenTest(t.session_id) : undefined} disabled={!onOpenTest}>
                      <ListItemText primary={new Date(t.at).toLocaleString()} />
                    </ListItemButton>
                  ))}
                </List>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        {isDraft ? (
          <Button color="error" onClick={onDiscard}>{isNew ? 'Cancel' : 'Discard draft'}</Button>
        ) : (
          <Button color="info" onClick={onClose}>Cancel</Button>
        )}
        <Box sx={{ flex: 1 }} />
        {isDraft && (
          <DisabledReason reason={problem}>
            <Button color="info" variant="outlined" disabled={saving || (!isNew && !dirty)} onClick={handleSaveDraft}>Save draft</Button>
          </DisabledReason>
        )}
        {savesToBayesPrice && (
          <DisabledReason reason={problem}>
            <Button color="info" variant="outlined" disabled={saving} onClick={() => onPublishToBayesPrice!(form)}>Save to Bayes Price skills</Button>
          </DisabledReason>
        )}
        <DisabledReason reason={problem}>
          <Button
            color="info"
            variant="contained"
            disabled={saving || (!isDraft && !dirty)}
            onClick={() => (isDraft ? onPublish(form) : onSaveChanges(form))}
          >
            {isDraft ? 'Save to My skills' : 'Save changes'}
          </Button>
        </DisabledReason>
      </DialogActions>
    </>
  )
}
